import React, { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import theme from './theme' // カスタムテーマをインポート
import { CssBaseline, CircularProgress, Box } from '@mui/material'
import { AuthProvider } from './contexts/AuthContext'

// i18n初期化
import './i18n'
import { PrivateRoute } from './components/layout/PrivateRoute'
import { Layout } from './components/layout/Layout'
import { AuthPage } from './pages/AuthPage'
import { RegisterPage } from './pages/RegisterPage'
import PasswordResetPage from './pages/PasswordResetPage'
import InvitationAcceptPage from './pages/InvitationAcceptPage'
import { UnauthorizedPage } from './pages/UnauthorizedPage'

import { DashboardPage } from './pages/DashboardPage'
import { CompliancePage } from './pages/CompliancePage'
import { TestPage } from './pages/TestPage'
import { MyEnrollmentsPage } from './pages/MyEnrollmentsPage'
import { TestManagePage } from './pages/TestManagePage'
import { UserManagePage } from './pages/UserManagePage'
// UserGroupManagePage は UserManagePage に統合されました
import ProfilePage from './pages/ProfilePage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import EmailTemplateManagePage from './pages/EmailTemplateManagePage'
import ReminderManagePage from './pages/ReminderManagePage'
import TenantReminderManagePage from './pages/TenantReminderManagePage'
import TenantManagementPage from './pages/TenantManagementPage'
import RoleFeatureManagement from './pages/RoleFeatureManagement'
import TestSchedulePage from './pages/TestSchedulePage'
import TestScheduleDetailPage from './pages/TestScheduleDetailPage'
import TestScheduleReportPage from './pages/TestScheduleReportPage'
import LearningPlanPage from './pages/LearningPlanPage'
import LearningPlanDetailPage from './pages/LearningPlanDetailPage'
import EmailDeliveryPage from './pages/EmailDeliveryPage'
import BillingManagementPage from './pages/BillingManagementPage'
import SubscriptionPage from './pages/SubscriptionPage'
import InvoiceHistoryPage from './pages/InvoiceHistoryPage'
import QuestionReportPage from './pages/QuestionReportPage'
import AnnouncementManagePage from './pages/AnnouncementManagePage'

// i18n読み込み中のローディング表示
const I18nLoadingFallback = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <CircularProgress />
  </Box>
)

function App() {
  return (
    <Suspense fallback={<I18nLoadingFallback />}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              {/* 認証不要のルート */}
              <Route path="/login" element={<AuthPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
              <Route path="/invite/accept/:token" element={<InvitationAcceptPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* 認証が必要な特別ルート（ナビゲーションなし） */}
              <Route
                path="/change-password"
                element={
                  <PrivateRoute>
                    <ChangePasswordPage />
                  </PrivateRoute>
                }
              />

              {/* 認証が必要なルート */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout />
                  </PrivateRoute>
                }
              >
                {/* ダッシュボード（全ユーザー） */}
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="my-enrollments" element={<MyEnrollmentsPage />} />
                <Route path="test/:testId" element={<TestPage />} />
                <Route path="profile" element={<ProfilePage />} />

                {/* HR管理者以上のルート */}
                <Route
                  path="compliance"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <CompliancePage />
                    </PrivateRoute>
                  }
                />
                {/* カテゴリ管理は規範・ガイドライン管理に統合 */}
                <Route path="categories" element={<Navigate to="/compliance" replace />} />
                <Route
                  path="tests"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <TestManagePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <UserManagePage />
                    </PrivateRoute>
                  }
                />
                {/* user-groups は users に統合されたため、リダイレクト */}
                <Route path="user-groups" element={<Navigate to="/users" replace />} />
                <Route
                  path="test-schedules"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <TestSchedulePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="test-schedules/:id"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <TestScheduleDetailPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="test-schedules/:id/report"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <TestScheduleReportPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="email-templates"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <EmailTemplateManagePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="reminder-manage"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN']}>
                      <ReminderManagePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="question-reports"
                  element={
                    <PrivateRoute
                      requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}
                      skipFeatureCheck
                    >
                      <QuestionReportPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="learning-plans"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <LearningPlanPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="learning-plans/:id"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <LearningPlanDetailPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="email-delivery"
                  element={
                    <PrivateRoute requiredRoles={['SUPER_ADMIN']}>
                      <EmailDeliveryPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="subscription"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <SubscriptionPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="invoices"
                  element={
                    <PrivateRoute requiredRoles={['CORPORATE_ADMIN', 'SUPER_ADMIN']}>
                      <InvoiceHistoryPage />
                    </PrivateRoute>
                  }
                />

                {/* SUPER_ADMIN専用のルート */}
                <Route
                  path="tenant-management"
                  element={
                    <PrivateRoute requiredRoles={['SUPER_ADMIN']}>
                      <TenantManagementPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="role-features"
                  element={
                    <PrivateRoute requiredRoles={['SUPER_ADMIN']}>
                      <RoleFeatureManagement />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="billing"
                  element={
                    <PrivateRoute requiredRoles={['SUPER_ADMIN']}>
                      <BillingManagementPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="tenant-reminder-manage"
                  element={
                    <PrivateRoute requiredRoles={['SUPER_ADMIN']}>
                      <TenantReminderManagePage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="announcements"
                  element={
                    <PrivateRoute requiredRoles={['SUPER_ADMIN']}>
                      <AnnouncementManagePage />
                    </PrivateRoute>
                  }
                />

                {/* デフォルトリダイレクト */}
                <Route index element={<Navigate to="/dashboard" replace />} />
              </Route>

              {/* 未定義のパスはダッシュボードにリダイレクト */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </Suspense>
  )
}

export default App
